import { Component, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ProductItem } from '../../model/product-item';
import { AdminService, adminFailure } from '../../service/admin.service';

/**
 * The product's photos: upload, add one by URL, reorder, delete. The first photo is the one the
 * product cards use, which is what the arrows are for.
 */
@Component({
  selector: 'admin-photos',
  imports: [FormsModule],
  host: {
    style: 'display: contents'
  },
  styles: `
    .admin-photo {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px 0;
      border-bottom: 1px solid #f0f0f0;
    }

    .admin-photo img {
      width: 64px;
      height: 64px;
      object-fit: cover;
    }

    .admin-photo-url {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .admin-field {
      display: block;
      margin: 12px 0;
    }

    .admin-field input {
      width: 100%;
      border: 1px solid #e6e6e6;
      padding: 6px 10px;
    }
    `,
  template: `
    <!-- Tracked by position, not by URL: reordering swaps the values in place, so there is no DOM
         to move and the rows simply re-render. -->
    @for (photo of photos(); track $index; let first = $first; let last = $last) {
      <div class="admin-photo">
        <img alt="" [src]="photo">
        <span class="admin-photo-url s-text8">{{photo}}</span>
        <button type="button" class="admin-photo-up" aria-label="Вгору" [disabled]="first || busy()"
          (click)="move(photo, 'up')">
          <span class="fa fa-arrow-up"></span>
        </button>
        <button type="button" class="admin-photo-down" aria-label="Вниз" [disabled]="last || busy()"
          (click)="move(photo, 'down')">
          <span class="fa fa-arrow-down"></span>
        </button>
        <button type="button" class="admin-photo-delete" aria-label="Видалити" [disabled]="busy()"
          (click)="remove(photo)">
          <span class="fa fa-times-circle"></span>
        </button>
      </div>
    } @empty {
      <p class="s-text8">Ще немає жодної фотографії.</p>
    }

    <label class="admin-field">
      Завантажити з комп'ютера
      <input type="file" multiple accept="image/*" [disabled]="busy()" (change)="upload($any($event.target))">
    </label>

    <form (submit)="addUrl($event)">
      <label class="admin-field">
        Або додати за посиланням
        <input name="url" type="url" placeholder="https://…" required [(ngModel)]="url">
      </label>
      <button type="submit" class="flex-c-m size9 bg1 bo-rad-23 hov1 s-text1 trans-0-4 admin-add-photo"
        [disabled]="busy()">
        Додати
      </button>
    </form>

    @if (busy()) {
      <p class="s-text8">Зачекайте…</p>
    }
    @if (failure(); as failure) {
      <p class="s-text5 text-danger p-t-10">{{failure}}</p>
    }
    `
})
export class AdminPhotosComponent {

  readonly product = input.required<ProductItem>();
  readonly changed = output<ProductItem>();

  private service = inject(AdminService);
  private updated = signal<ProductItem | undefined>(undefined);

  /** One call at a time: the rows shift under a second click, onto a different photo. */
  readonly busy = signal(false);
  readonly failure = signal<string | undefined>(undefined);

  url = '';

  photos() {
    return (this.updated() ?? this.product()).photos;
  }

  async upload(input: HTMLInputElement) {
    if (input.files?.length) {
      await this.run(() => this.service.addPhotos(this.product().id, input.files!));
      // Otherwise picking the same files again, to retry, fires no change.
      input.value = '';
    }
  }

  async addUrl(event: Event) {
    event.preventDefault();
    if (await this.run(() => this.service.addPhotoUrl(this.product().id, this.url))) {
      this.url = '';
    }
  }

  move(photo: string, direction: 'up' | 'down') {
    return this.run(() => this.service.movePhoto(this.product().id, photo, direction));
  }

  remove(photo: string) {
    return this.run(() => this.service.deletePhoto(this.product().id, photo));
  }

  private async run(action: () => Promise<ProductItem>): Promise<boolean> {
    if (this.busy()) {
      return false;
    }
    this.busy.set(true);
    this.failure.set(undefined);
    try {
      const product = await action();
      this.updated.set(product);
      this.changed.emit(product);
      return true;
    } catch (error) {
      this.failure.set(adminFailure(error, 'Не вдалося змінити фотографії. Спробуйте ще раз.'));
      return false;
    } finally {
      this.busy.set(false);
    }
  }

}
